import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import printPdf from 'utils/printPdf';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import IconButton from 'components/common/IconButton';
import Menu from 'components/common/Menu';
import DeleteMenu from 'components/common/DeleteMenu';
import Button from 'components/common/Button';
import IconTrashCan from 'components/icons/IconTrashCan';
import IconPrint from 'components/icons/IconPrint';

import formatUtcToLocalDate from 'utils/formatUtcToLocalDate';
import splitLongWords from 'utils/splitLongWords';
import templateToPDF from 'utils/templateToPDF';

import pxToRem from 'styles/pxToRem';

import dateFormat from 'config/dateFormat';
import timeDateFormat from 'config/timeDateFormat';

import { deleteTemplate } from 'redux/modules/templates';

const styles = theme => ({
  root: {
    width: 288,
  },
  paper: {
    boxSizing: 'border-box',
    padding: 24,
    height: '100%',
    borderRadius: 6,
    border: `1px solid ${theme.palette.primary.A100}`,
  },
  buttonPrint: {
    margin: '-12px 0 0 0',
  },
  buttonRemove: {
    margin: '-12px -12px 0 0',
  },
  iconTrashCan: {
    width: 14,
    height: 18,
  },
  iconPrint: {
    width: 22,
    height: 20,
  },
  typographyTitle: {
    marginTop: 24,
    minHeight: 48,
    textTransform: 'uppercase',
  },
  typography: {
    fontSize: pxToRem(13),
  },
  buttonEdit: {
    marginTop: 28,
  },
  typographyNoAuthor: {
    marginTop: 24,
    marginBottom: 0,
  },
});

class TemplatesListItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
    };

    this.onMenuClose = this.onMenuClose.bind(this);
    this.onRemoveButtonClick = this.onRemoveButtonClick.bind(this);
    this.onRemoveCancel = this.onRemoveCancel.bind(this);
    this.onRemoveSubmit = this.onRemoveSubmit.bind(this);
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onRemoveButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
    });
  }

  onRemoveCancel() {
    this.setState({ anchorEl: null });
  }

  onRemoveSubmit() {
    const { ID, actions } = this.props;
    actions.deleteTemplate({ ID });
    this.setState({ anchorEl: null });
  }

  render() {
    const { account, Author, Name, classes, business, history, ...otherProps } = this.props;
    const { anchorEl } = this.state;
    const open = Boolean(anchorEl);
    return (
      <Grid
        className={classes.root}
        item
      >
        <Paper className={classes.paper}>
          <Grid
            container
            justify="flex-end"
          >
            <Grid item>
              <IconButton
                className={classes.buttonPrint}
                onClick={() => {
                  templateToPDF({ template: this.props, business }).then(PDF => ({
                    blob: PDF.output('blob'),
                    Name,
                  })).then((values) => {
                    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                      window.navigator.msSaveOrOpenBlob(values.blob, `${values.Name}.pdf`);
                    } else {
                      printPdf(values.blob);
                    }
                  });
                }}
                tooltipContent="Print"
              >
                <IconPrint className={classes.iconPrint} />
              </IconButton>
            </Grid>
            <Grid item>
              <IconButton
                className={classes.buttonRemove}
                aria-label="Remove"
                aria-owns={open ? `${otherProps.ID}-template-remove-menu` : null}
                aria-haspopup="true"
                disabled={account.Roles.filter(item =>
                  item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length === 0}
                onClick={this.onRemoveButtonClick}
                tooltipContent="Delete"
              >
                <IconTrashCan className={classes.iconTrashCan} />
              </IconButton>
              <Menu
                id={`${otherProps.ID}-template-remove-menu`}
                anchorEl={anchorEl}
                open={open}
                onClose={this.onMenuClose}
                list={false}
              >
                <DeleteMenu
                  text="Are you sure you want to delete this template?"
                  onCancel={this.onRemoveCancel}
                  onSubmit={this.onRemoveSubmit}
                />
              </Menu>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={24}
          >
            <Grid
              item
              xs={12}
            >
              <Typography
                className={classes.typographyTitle}
                color="secondary"
              >
                {splitLongWords(Name)}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={24}
            alignItems="flex-end"
          >
            <Grid
              item
              xs={7}
            >
              <Typography
                className={classNames(classes.typography, {
                  [classes.typographyNoAuthor]: !Author['First Name'],
                })}
                color="secondary"
                gutterBottom
              >
                {Author['First Name'] ?
                  'Created by' : 'Created on'}
              </Typography>
              {Author['First Name'] &&
                <Typography className={classes.typography}>
                  {Author['First Name']
                    ? `${Author['First Name']} ${Author['Last Name']}`
                    : ''}
                </Typography>
              }
            </Grid>
            <Grid
              item
              xs={5}
            >
              <Typography
                className={classes.typography}
                align="right"
              >
                {formatUtcToLocalDate(
                  otherProps['Create At'],
                  timeDateFormat,
                  dateFormat,
                  business['Company Address']['Time Zone'],
                  business['Company Address']['Observes Daylight Saving'],
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={24}
            justify="space-between"
          >
            <Grid item>
              <Button
                className={classes.buttonEdit}
                variant="raised"
                size="md"
                onClick={() => {
                  history.push('/settings/templates/create', {
                    initialValues: {
                      Name: `${Name} Copy`,
                      Questions: otherProps.Questions.map(({ ID, Text, ...otherValues }) => ({
                        ...Text ? { Text } : {},
                        ...otherValues,
                      })),
                    },
                  });
                }}
              >
                Copy
              </Button>
            </Grid>
            <Grid item>
              <Button
                className={classes.buttonEdit}
                variant="raised"
                color="primary"
                size="md"
                href={`/settings/templates/${otherProps.ID}`}
              >
                Edit
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    );
  }
}

TemplatesListItem.propTypes = {
  ID: PropTypes.number.isRequired,
  Name: PropTypes.string.isRequired,
  Author: PropTypes.shape({
    'First Name': PropTypes.string,
    'Last Name': PropTypes.string,
  }).isRequired,
  'Create At': PropTypes.string.isRequired,
  actions: PropTypes.shape({
    deleteTemplate: PropTypes.func.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  business: PropTypes.shape({
    ID: PropTypes.number,
    'Time Zone': PropTypes.string,
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    paper: PropTypes.string.isRequired,
    buttonRemove: PropTypes.string.isRequired,
    iconTrashCan: PropTypes.string.isRequired,
    typographyTitle: PropTypes.string.isRequired,
    typography: PropTypes.string.isRequired,
    buttonEdit: PropTypes.string.isRequired,
    typographyNoAuthor: PropTypes.string.isRequired,
  }).isRequired,
};

TemplatesListItem.defaultProps = {
  business: {},
};

const mapStateToProps = store => ({
  account: store.account,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ deleteTemplate }, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(TemplatesListItem),
));



// WEBPACK FOOTER //
// ./src/components/settings/TemplatesList/TemplatesListItem.js